import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Bold, Italic, List, Check } from "lucide-react";
import { looksLikeHtml, markdownToHtml, sanitizeHtml } from "@/lib/noteFormat";

/**
 * NoteEditor — WYSIWYG note editor for a library item.
 *
 * ROOT CAUSE of the previous bug the user hit THREE TIMES: the old editor
 * was a plain <textarea> that received markdown syntax (**bold**, *italic*)
 * from the toolbar — so you literally saw asterisks while typing. Preview
 * rendered correctly but the WRITING experience was broken. This component
 * uses a contentEditable div + document.execCommand so Bold/Italic/List
 * produce real formatting on screen immediately. The stored value is HTML
 * (not markdown); legacy notes auto-upgrade via markdownToHtml() on open.
 *
 * - Opens with cursor placed at end of existing content.
 * - Auto-saves 650ms after the last keystroke; shows a "Saved" pill.
 * - Esc + backdrop + X + Done all close (flush pending save first).
 * - Formatting toolbar is only Bold / Italic / Bullet list — nothing else.
 * - "Last edited" stamp beneath the editing area.
 * - Rendered HTML is sanitised server-side-equivalent on every save (strict
 *   allow-list) so pastes from any source can't inject anything hostile.
 */
export default function NoteEditor({ item, onClose, onSaved }) {
  const editorRef = useRef(null);
  const [status, setStatus] = useState("idle");  // idle | saving | saved | error
  const [editedAt, setEditedAt] = useState(item?.note_edited_at || null);
  const [isEmpty, setIsEmpty] = useState(true);
  const saveTimer = useRef(null);
  const latestHtml = useRef("");
  const initialHtml = useRef("");

  // Build the initial HTML ONCE, from either an existing HTML note or a
  // legacy markdown note. Both branches end up as sanitised HTML.
  useEffect(() => {
    const stored = item?.note || "";
    const html = sanitizeHtml(looksLikeHtml(stored) ? stored : markdownToHtml(stored));
    initialHtml.current = html;
    latestHtml.current = html;
    if (editorRef.current) {
      editorRef.current.innerHTML = html;
      // Focus + place caret at end so appending is instant.
      placeCaretAtEnd(editorRef.current);
      setIsEmpty(!editorRef.current.textContent?.trim());
    }
    // Intentionally only runs on mount — the editor is a controlled
    // contentEditable, so React doesn't own its innerHTML after this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esc to close.
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") close(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide the global Add FAB while the modal is open — the brass "+" button
  // was ghosting through the backdrop and reading as an "Add" button
  // inside the pop-up. The CSS rule lives in index.css.
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  const save = useCallback(async (htmlToSave) => {
    try {
      setStatus("saving");
      const r = await api.patch(`/items/${item.id}`, { note: htmlToSave });
      if (latestHtml.current === htmlToSave) {
        setStatus("saved");
        setEditedAt(new Date().toISOString());
      }
      onSaved?.(r.data);
    } catch (e) {
      console.error("[note-editor] save failed:", e);
      setStatus("error");
    }
  }, [item.id, onSaved]);

  function onInput() {
    const el = editorRef.current;
    if (!el) return;
    const dirty = sanitizeHtml(el.innerHTML);
    latestHtml.current = dirty;
    setIsEmpty(!el.textContent?.trim());
    clearTimeout(saveTimer.current);
    setStatus("saving");
    saveTimer.current = setTimeout(() => save(dirty), 650);
  }

  function close() {
    clearTimeout(saveTimer.current);
    // Flush any pending change before closing so Done ≡ always-saved.
    if (latestHtml.current !== initialHtml.current) {
      save(latestHtml.current).finally(() => onClose?.());
    } else {
      onClose?.();
    }
  }

  // Toolbar actions — use the native rich-text command. execCommand is
  // marked deprecated but every browser we support (incl. Safari & modern
  // Firefox) still implements it; the proposed replacement (Input Events
  // Level 2) is not yet widely available. The result renders inline in the
  // editor — the user sees bold/italic text, never asterisks.
  function exec(cmd) {
    editorRef.current?.focus();
    document.execCommand(cmd, false);
    onInput();
  }

  // Paste handler — strip formatting from pasted content so the note always
  // uses our palette/typography and can't carry hostile markup.
  function onPaste(e) {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
  }

  const statusCopy =
    status === "saving" ? "saving…" :
    status === "saved"  ? "saved" :
    status === "error"  ? "save failed — keep typing" :
    "";

  return (
    <div
      className="fixed inset-0 z-50 bg-[rgba(42,30,20,0.72)] backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-6"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Edit note"
      data-testid="note-editor-backdrop"
    >
      <div
        className="w-full sm:max-w-2xl bg-[var(--card-bg)] sm:rounded-lg border-y sm:border border-[rgba(140,106,47,0.3)] flex flex-col max-h-screen sm:max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="note-editor"
      >
        {/* Header: cover + title + close */}
        <header className="flex items-center gap-4 p-4 sm:p-5 border-b border-[rgba(140,106,47,0.18)]">
          {item.image_url ? (
            <img src={item.image_url} alt="" className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded border border-[rgba(140,106,47,0.2)] shrink-0" />
          ) : (
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[var(--paper-aged)] rounded border border-[rgba(140,106,47,0.2)] shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-cinzel text-[9px] tracking-[0.26em] uppercase text-[var(--ink-dusty)]">
              a personal note — private
            </p>
            <h2 className="font-serif-lit text-xl sm:text-2xl text-[var(--ink)] truncate" data-testid="note-editor-title">
              {item.title || "Untitled"}
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="close"
            data-testid="note-editor-close"
            className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full text-[var(--ink-dusty)] hover:text-[var(--bronze)] hover:bg-[var(--paper-aged)]"
          >
            <X size={18} strokeWidth={1.6} />
          </button>
        </header>

        {/* Toolbar — real formatting, no asterisks. */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-[rgba(140,106,47,0.14)] bg-[var(--paper-aged)]/40" data-testid="note-editor-toolbar">
          <ToolButton onClick={() => exec("bold")} label="bold" testId="note-editor-bold"><Bold size={14} strokeWidth={1.8}/></ToolButton>
          <ToolButton onClick={() => exec("italic")} label="italic" testId="note-editor-italic"><Italic size={14} strokeWidth={1.8}/></ToolButton>
          <ToolButton onClick={() => exec("insertUnorderedList")} label="bullet list" testId="note-editor-bullet"><List size={14} strokeWidth={1.8}/></ToolButton>
          <span className="flex-1" />
          <span
            className={`inline-flex items-center gap-1 font-mono-lc text-[10px] tracking-[0.14em] ${
              status === "saved" ? "text-[var(--sage)]" : status === "error" ? "text-[var(--terracotta)]" : "text-[var(--ink-dusty)]"
            }`}
            aria-live="polite"
            data-testid="note-editor-status"
          >
            {status === "saved" && <Check size={11} strokeWidth={2}/>}
            {statusCopy}
          </span>
        </div>

        {/* Writing area — contentEditable. Shows a placeholder when empty. */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 relative">
          {isEmpty && (
            <p
              aria-hidden="true"
              className="pointer-events-none absolute top-4 sm:top-6 left-4 sm:left-6 font-garamond text-[17px] sm:text-[18px] italic text-[var(--ink-light)]"
              data-testid="note-editor-placeholder"
            >
              Jot down what this meant to you.
            </p>
          )}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={onInput}
            onPaste={onPaste}
            data-testid="note-editor-content"
            className="note-editor-surface w-full min-h-[40vh] bg-transparent outline-none font-garamond text-[17px] sm:text-[18px] text-[var(--ink)] leading-[1.7]"
          />
          {editedAt && (
            <p className="mt-6 font-mono-lc text-[10px] tracking-[0.18em] uppercase text-[var(--ink-light)]" data-testid="note-editor-last-edited">
              last edited {formatEdited(editedAt)}
            </p>
          )}
        </div>

        {/* Footer: Done */}
        <footer className="p-4 border-t border-[rgba(140,106,47,0.18)] flex justify-end">
          <button
            type="button"
            onClick={close}
            data-testid="note-editor-done"
            className="lc-btn"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

function ToolButton({ onClick, label, testId, children }) {
  return (
    <button
      type="button"
      // Use mousedown so the selection in the contentEditable isn't lost
      // when the button grabs focus — classic rich-text-editor gotcha.
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      aria-label={label}
      title={label}
      data-testid={testId}
      className="w-8 h-8 flex items-center justify-center rounded text-[var(--ink-dusty)] hover:text-[var(--ink)] hover:bg-[var(--paper-aged)] transition-colors"
    >
      {children}
    </button>
  );
}

function placeCaretAtEnd(el) {
  el.focus();
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

function formatEdited(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }).toLowerCase();
  } catch { return ""; }
}
